import { invoke } from "@tauri-apps/api/core";
import { getFloatingFrameDisplayArea } from "./floating-frame-displays";
import {
  SETTINGS_POCKET_WIDTH,
  lastTrayPanelSize,
  positionSettingsPocket,
  settingsPocketMaxHeight,
  translateOverlayRect,
  trayPanelRect,
  type SettingsPocketPlacement,
  type TrayPanelAnchor,
  type TrayPanelRect,
} from "./tray-panel-geometry";
import { create } from "zustand";
import { emit, listen } from "@tauri-apps/api/event";
import {
  settingsStackFor,
  type SettingsTarget,
} from "@/components/settings/settings-navigation";
import type { FocusWindowBox } from "@/lib/focus-window-layout";
import { isTauri } from "@/lib/is-tauri";
import { getWindowDisplayId } from "./use-window-role";
import { getWindowOrigin } from "./use-window-origin";
import { listenForSettingsDisplayRequests } from "./settings-display-request";
import {
  combinedOpener,
  windowOpenerFrom,
  type WindowOpener,
} from "./window-opener";
import {
  getDesktopChatAuthScope,
  isCurrentDesktopChatAuthScope,
  subscribeDesktopChatAuthScope,
  type DesktopChatAuthScope,
} from "./desktop-chat-auth-scope";
import { safeTauriUnlisten } from "./tauri-listener-cleanup";
import {
  getSettingsDraftSnapshot,
  hydrateSettingsDraftSnapshot,
  clearSettingsDraftSession,
  clearAllSettingsDrafts,
} from "@/components/settings/settings-draft-state";

import type { SettingsFramePreview } from "./settings-frame-preview";

export interface SettingsFrame {
  sessionId: string;
  displayId: number;
  stack: SettingsTarget[];
  /** The room the pocket may fill; its height follows content up to the box's. */
  box: FocusWindowBox;
  /** The box edge the pocket hangs from; it grows toward the opposite one. */
  pinnedEdge: SettingsPocketPlacement["pinnedEdge"];
  /** Who opened the pocket: only the person's open takes the keyboard. */
  openedBy: WindowOpener;
  dragging: boolean;
  previewId?: string;
  preview?: SettingsFramePreview;
}
interface Update {
  previewSnapshot?: SettingsFramePreview;
  frame: SettingsFrame | null;
  revision: number;
  origin: string;
  authScope: DesktopChatAuthScope | null;
  draftSnapshot?: Record<string, unknown>;
  draftRevision?: number;
  closedSessionId?: string;
}
const EVENT = "denker://settings-frame-update";
const REQUEST = "denker://settings-frame-request";
const RELOCATE = "denker://settings-frame-relocate";
const ACK = "denker://settings-frame-draft-ready";
interface DraftReady {
  sessionId: string;
  draftRevision: number;
  authScope: DesktopChatAuthScope | null;
}
interface RelocateRequest {
  sessionId: string;
  displayId: number;
  stack: SettingsTarget[];
  box: FocusWindowBox;
  pinnedEdge: SettingsFrame["pinnedEdge"];
  openedBy: WindowOpener;
  authScope: DesktopChatAuthScope | null;
}
let latest: Update | null = null;
let openGeneration = 0;
const draftRevisions = new Map<string, number>();
const retiredSessions = new Set<string>();
const previews = new Map<string, SettingsFramePreview>();
export const useSettingsFrameStore = create<{ frame: SettingsFrame | null }>(
  () => ({ frame: null }),
);

export function applySettingsFrameUpdate(update: Update): boolean {
  if (
    !isCurrentDesktopChatAuthScope(update.authScope) ||
    !Number.isFinite(update.revision)
  )
    return false;
  if (!update.frame && update.closedSessionId) {
    retiredSessions.add(update.closedSessionId);
    clearSettingsDraftSession(update.closedSessionId);
    draftRevisions.delete(update.closedSessionId);
    if (
      useSettingsFrameStore.getState().frame?.sessionId ===
      update.closedSessionId
    ) {
      latest = {
        ...update,
        revision: Math.max(update.revision, latest?.revision ?? 0),
      };
      useSettingsFrameStore.setState({ frame: null });
      return true;
    }
    if (useSettingsFrameStore.getState().frame) return false;
  }
  if (update.frame && retiredSessions.has(update.frame.sessionId)) return false;
  if (update.frame?.previewId && update.previewSnapshot) {
    previews.set(update.frame.previewId, update.previewSnapshot);
    // Bound visual-only snapshots across repeated drags.
    if (previews.size > 16) previews.delete(previews.keys().next().value!);
    const current = useSettingsFrameStore.getState().frame;
    if (current?.previewId === update.frame.previewId && !current.preview)
      useSettingsFrameStore.setState({
        frame: { ...current, preview: update.previewSnapshot },
      });
  }
  if (
    latest &&
    (update.revision < latest.revision ||
      (update.revision === latest.revision && update.origin <= latest.origin))
  )
    return false;
  latest = update;
  if (
    update.frame &&
    update.draftSnapshot &&
    (update.draftRevision ?? update.revision) >
      (draftRevisions.get(update.frame.sessionId) ?? -1)
  ) {
    hydrateSettingsDraftSnapshot(update.frame.sessionId, update.draftSnapshot);
    draftRevisions.set(
      update.frame.sessionId,
      update.draftRevision ?? update.revision,
    );
  }
  if (!update.frame) {
    const previous = useSettingsFrameStore.getState().frame;
    if (previous) {
      clearSettingsDraftSession(previous.sessionId);
      draftRevisions.delete(previous.sessionId);
    }
  }
  useSettingsFrameStore.setState({
    frame: update.frame
      ? {
          ...update.frame,
          preview: update.frame.previewId
            ? previews.get(update.frame.previewId)
            : undefined,
        }
      : null,
  });
  if (
    update.frame?.displayId === getWindowDisplayId() &&
    update.draftSnapshot
  ) {
    void emit(ACK, {
      sessionId: update.frame.sessionId,
      draftRevision: update.draftRevision ?? update.revision,
      authScope: update.authScope,
    } satisfies DraftReady).catch(() => {});
  }
  return true;
}

export function putSettingsFrame(frame: SettingsFrame | null): void {
  const previous = useSettingsFrameStore.getState().frame;
  const revision = Math.max(Date.now() * 1000, (latest?.revision ?? 0) + 1);
  const transferred = frame && previous?.displayId !== frame.displayId;
  const { preview, ...geometry } = frame ?? {};
  const update: Update = {
    frame: frame ? (geometry as SettingsFrame) : null,
    ...(frame?.previewId !== previous?.previewId && preview
      ? { previewSnapshot: preview }
      : {}),
    revision,
    origin: String(getWindowDisplayId()),
    authScope: getDesktopChatAuthScope(),
    ...(!frame && previous ? { closedSessionId: previous.sessionId } : {}),
    ...(transferred
      ? {
          draftSnapshot: getSettingsDraftSnapshot(frame.sessionId),
          draftRevision: revision,
        }
      : frame && latest?.frame?.sessionId === frame.sessionId
        ? {
            draftSnapshot: latest.draftSnapshot,
            draftRevision: latest.draftRevision,
          }
        : {}),
  };
  if (!applySettingsFrameUpdate(update)) return;
  if (isTauri()) void emit(EVENT, update).catch(() => {});
}

const EDGE = 8;

/** Where the tray panel is, or would be, in this display's overlay coordinates. */
async function trayRectOnDisplay(
  displayId: number,
  viewport: { width: number; height: number },
): Promise<TrayPanelRect | null> {
  const anchor = await invoke<TrayPanelAnchor | null>(
    "get_tray_icon_rect",
  ).catch(() => null);
  if (!anchor) return null;
  const size = lastTrayPanelSize();
  if (anchor.displayId === displayId)
    return trayPanelRect(anchor, size, viewport);
  const area = getFloatingFrameDisplayArea(anchor.displayId);
  const trayViewport = area
    ? { width: area.width, height: area.height }
    : viewport;
  return translateOverlayRect(
    trayPanelRect(anchor, size, trayViewport),
    trayViewport,
    viewport,
  );
}

/**
 * Open the pocket at `target` on this window's display (spec §4): left of
 * the tray panel with a 12px gap, or on the tray's right when the left has
 * no room; it shares the tray's top edge, or its bottom edge above a bottom
 * taskbar. `trayRect` is the open tray's own rect (the profile button passes
 * it); every other entry point places the pocket where the tray would be.
 * An open pocket keeps its place and only navigates. `opener` says who
 * asked: a pocket the person opened takes the keyboard, one an agent or
 * Denker opened never does.
 */
export async function openSettingsFrame(
  target: SettingsTarget,
  opener: WindowOpener,
  trayRect?: TrayPanelRect,
): Promise<void> {
  const generation = ++openGeneration;
  const authScope = getDesktopChatAuthScope();
  const displayId = getWindowDisplayId();
  if (displayId === null) return;
  const stack = settingsStackFor(target, "desktop");
  const openHere = useSettingsFrameStore.getState().frame;
  if (openHere?.displayId === displayId) {
    putSettingsFrame({
      ...openHere,
      stack,
      openedBy: combinedOpener(openHere.openedBy, opener),
    });
    return;
  }
  const viewport = { width: window.innerWidth, height: window.innerHeight };
  const tray = trayRect ?? (await trayRectOnDisplay(displayId, viewport));
  if (
    generation !== openGeneration ||
    !isCurrentDesktopChatAuthScope(authScope)
  )
    return;
  const { pinnedEdge, ...placement }: SettingsPocketPlacement = tray
    ? positionSettingsPocket(tray, viewport)
    : {
        left: Math.max(EDGE, viewport.width - SETTINGS_POCKET_WIDTH - EDGE),
        top: EDGE,
        maxHeight: settingsPocketMaxHeight(EDGE, viewport.height),
        pinnedEdge: "top",
      };
  const origin = getWindowOrigin();
  const box = {
    x: origin.x + placement.left,
    y: origin.y + placement.top,
    width: SETTINGS_POCKET_WIDTH,
    height: placement.maxHeight,
  };
  const current = useSettingsFrameStore.getState().frame;
  if (current?.displayId === displayId) {
    putSettingsFrame({
      ...current,
      stack,
      openedBy: combinedOpener(current.openedBy, opener),
    });
    return;
  }
  if (current) {
    void emit(RELOCATE, {
      sessionId: current.sessionId,
      displayId,
      stack,
      box,
      pinnedEdge,
      openedBy: combinedOpener(current.openedBy, opener),
      authScope: getDesktopChatAuthScope(),
    } satisfies RelocateRequest).catch(() => {});
    return;
  }
  putSettingsFrame({
    sessionId: crypto.randomUUID(),
    displayId,
    stack,
    dragging: false,
    box,
    pinnedEdge,
    openedBy: opener,
  });
}

export function setSettingsFrameStack(stack: SettingsTarget[]): void {
  const frame = useSettingsFrameStore.getState().frame;
  if (frame) putSettingsFrame({ ...frame, stack });
}

export function closeSettingsFrame(): void {
  openGeneration++;
  putSettingsFrame(null);
}

/** Mounted before the lazy app on every display, like floating frame transport. */
export function attachSettingsFrames(): () => void {
  if (!isTauri()) return () => {};
  let disposed = false;
  const unlisteners: Array<() => void> = [];
  const add = (cleanup: () => void) => {
    if (disposed) safeTauriUnlisten(cleanup);
    else unlisteners.push(cleanup);
  };
  const ready = Promise.all([
    listenForSettingsDisplayRequests((target, opener) =>
      openSettingsFrame(target, opener),
    ).then(add),
    listen<Update>(EVENT, ({ payload }) =>
      applySettingsFrameUpdate(payload),
    ).then(add),
    listen<DraftReady>(ACK, ({ payload }) => {
      if (
        latest?.frame?.sessionId === payload.sessionId &&
        latest.draftRevision === payload.draftRevision &&
        isCurrentDesktopChatAuthScope(payload.authScope)
      ) {
        latest = { ...latest, draftSnapshot: undefined };
      }
    }).then(add),
    listen<RelocateRequest>(RELOCATE, ({ payload }) => {
      const frame = useSettingsFrameStore.getState().frame;
      if (
        !frame ||
        frame.sessionId !== payload.sessionId ||
        frame.displayId !== getWindowDisplayId() ||
        frame.dragging ||
        !isCurrentDesktopChatAuthScope(payload.authScope)
      )
        return;
      // Only the currently mounted owner can snapshot the latest form edits.
      putSettingsFrame({
        ...frame,
        displayId: payload.displayId,
        stack: payload.stack,
        box: payload.box,
        pinnedEdge: payload.pinnedEdge,
        openedBy: windowOpenerFrom(payload.openedBy),
      });
    }).then(add),
    listen(REQUEST, () => {
      if (!latest) return;
      if (latest.frame && latest.frame.displayId !== getWindowDisplayId())
        return;
      void emit(
        EVENT,
        latest.frame
          ? {
              ...latest,
              previewSnapshot: useSettingsFrameStore.getState().frame?.preview,
              draftSnapshot: getSettingsDraftSnapshot(latest.frame.sessionId),
              draftRevision: latest.revision,
            }
          : latest,
      ).catch(() => {});
    }).then(add),
  ]);
  const reset = subscribeDesktopChatAuthScope(() => {
    openGeneration++;
    latest = null;
    draftRevisions.clear();
    retiredSessions.clear();
    previews.clear();
    clearAllSettingsDrafts();
    useSettingsFrameStore.setState({ frame: null });
    void emit(REQUEST).catch(() => {});
  });
  void ready
    .then(() => {
      if (!disposed) return emit(REQUEST);
    })
    .catch(() => {});
  return () => {
    disposed = true;
    reset();
    unlisteners.forEach(safeTauriUnlisten);
  };
}
